// VIS-197: the sums every line and area on the map shares: nearest points, inside, length, area, turn, resampling, the
// bulbs of a fence. Pure functions of [x, z] pairs (local metres, x east, z south): no three.js, no DOM, safe for the
// always-loaded engine, the lazy modules, the package check and the portal (@mapmystay/lines). Every guest's page
// fetches this file (fenceroad.js, ground.js): what only drawing and editing need is in shape.js and simplify.js.
// scripts/ci/check-one-geometry.mjs counts copies of these outside web/js/lines/: that count may only fall.

/** @typedef {[number, number]} Pt */
/** @typedef {ReadonlyArray<readonly [number, number]>} Pts */

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/**
 * Distance from p to a polyline, and the arc length `s` of the nearest point along it.
 * @param {Pts} pts
 * @param {readonly [number, number]} p
 */
export function nearestOn(pts, p) {
  let best = Infinity, at = 0, s = 0;
  for (let i = 1; i < pts.length; i++) {
    const [ax, az] = pts[i - 1], [bx, bz] = pts[i], vx = bx - ax, vz = bz - az, L2 = vx * vx + vz * vz || 1, L = Math.sqrt(L2);
    const t = Math.max(0, Math.min(1, ((p[0] - ax) * vx + (p[1] - az) * vz) / L2));
    const d = Math.hypot(ax + vx * t - p[0], az + vz * t - p[1]);
    if (d < best) { best = d; at = s + t * L; }
    s += L;
  }
  return { d: best, s: at };
}

/**
 * VIS-219: how far along the stretch (ax, az)-(bx, bz) the point nearest to (x, z) lies, 0..1 (a stretch of no length:
 * 0). Scalars in, a number out: the tree rules ask it for every tree, so nothing is allocated.
 */
export function stretchT(x, z, ax, az, bx, bz) {
  const vx = bx - ax, vz = bz - az;
  return Math.max(0, Math.min(1, ((x - ax) * vx + (z - az) * vz) / (vx * vx + vz * vz || 1)));
}
/** VIS-219: the distance from (x, z) to the stretch (ax, az)-(bx, bz). */
export function stretchDist(x, z, ax, az, bx, bz) {
  const t = stretchT(x, z, ax, az, bx, bz);
  return Math.hypot(ax + (bx - ax) * t - x, az + (bz - az) * t - z);
}

/** The distance from (x, z) to a line, or to an outline when `closed` (its last corner joined to its first). */
export function lineDist(x, z, pts, closed = false) {
  let d = Infinity;
  for (let i = 0, n = pts.length, m = closed ? n : n - 1; i < m; i++) {
    const [ax, az] = pts[i], [bx, bz] = pts[(i + 1) % n], vx = bx - ax, vz = bz - az, t = Math.max(0, Math.min(1, ((x - ax) * vx + (z - az) * vz) / (vx * vx + vz * vz || 1)));
    d = Math.min(d, Math.hypot(ax + vx * t - x, az + vz * t - z));
  }
  return d;
}

/**
 * WS-88: "is p nearer than reach(line) to any of these lines?" without walking every segment of every line. Each
 * segment is filed under the cells (C m) its box grown by its line's reach covers, so a point within reach of a
 * segment always finds it in its own cell. The distance is nearestOn's, term for term: the same answer, bit for bit.
 * `skip` = a line left out of a query. Returns near(p, skip) -> boolean.
 */
export function nearAny(lines, reach, C = 8) {
  const cells = new Map(), R = lines.map((r) => reach(r));
  lines.forEach((r, li) => {
    const P = r.pts, pad = R[li];
    if (!(pad > 0) || !(P?.length > 1)) return;
    for (let i = 1; i < P.length; i++) {
      const [ax, az] = P[i - 1], [bx, bz] = P[i];
      const x0 = Math.floor((Math.min(ax, bx) - pad) / C), x1 = Math.floor((Math.max(ax, bx) + pad) / C), z0 = Math.floor((Math.min(az, bz) - pad) / C), z1 = Math.floor((Math.max(az, bz) + pad) / C);
      for (let x = x0; x <= x1; x++) for (let z = z0; z <= z1; z++) { const k = x * 65536 + z; let l = cells.get(k); if (!l) cells.set(k, (l = [])); l.push(li, i); }
    }
  });
  return (p, skip = null) => {
    const l = cells.get(Math.floor(p[0] / C) * 65536 + Math.floor(p[1] / C));
    if (!l) return false;
    for (let j = 0; j < l.length; j += 2) {
      const r = lines[l[j]];
      if (r === skip) continue;
      const P = r.pts, i = l[j + 1], [ax, az] = P[i - 1], [bx, bz] = P[i], vx = bx - ax, vz = bz - az, L2 = vx * vx + vz * vz || 1;
      const t = Math.max(0, Math.min(1, ((p[0] - ax) * vx + (p[1] - az) * vz) / L2));
      if (Math.hypot(ax + vx * t - p[0], az + vz * t - p[1]) < R[l[j]]) return true;
    }
    return false;
  };
}

/** Whether (x, z) lies inside the ring (even-odd rule; the ring may or may not repeat its first vertex). */
export function inRing(x, z, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, zi] = ring[i], [xj, zj] = ring[j];
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

// VIS-219 (from main.js, WS-19 load time): the inside test with a bounding-box precheck, the box worked out once per
// ring. The same answer as inRing (a point outside the box has no crossing on its right, or an even number), but the
// tree rules ask it for each of ~120k trees against paddock, area and zone rings: most answers cost four compares.
const boxes = new WeakMap();
/** Whether (x, z) lies in the ring's bounding box grown by `m` metres (the box is kept per ring). */
export function inRingBox(ring, x, z, m = 0) {
  let b = boxes.get(ring);
  if (!b) { b = [Infinity, Infinity, -Infinity, -Infinity]; for (const [px, pz] of ring) { if (px < b[0]) b[0] = px; if (pz < b[1]) b[1] = pz; if (px > b[2]) b[2] = px; if (pz > b[3]) b[3] = pz; } boxes.set(ring, b); }
  const e = m + 1e-6;
  return x >= b[0] - e && x <= b[2] + e && z >= b[1] - e && z <= b[3] + e;
}
/** inRing, the box asked first (for rings asked many times). */
export const inRingFast = (x, z, ring) => inRingBox(ring, x, z) && inRing(x, z, ring);

/** Twice the signed area of a ring (the shoelace sum): positive when it runs clockwise seen from above (x east, z south). */
export function shoelace2(ring) {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return s;
}
/** Square metres inside an outline. @param {Pts} ring */
export const areaOf = (ring) => Math.abs(shoelace2(ring)) / 2;
/** Which way a loop runs seen from above with north up: 'cw', 'ccw', or null for fewer than 3 points or no area. @param {Pts} ring @returns {'cw' | 'ccw' | null} */
export function turnOf(ring) {
  if (ring.length < 3) return null;
  const s = shoelace2(ring);
  return s > 0 ? 'cw' : s < 0 ? 'ccw' : null;
}

/** Metres along a line on the map; a loop (`closed`) counts the side from its last point back to its first. @param {Pts} pts */
export function lengthOf(pts, closed = false) {
  let m = 0;
  for (let i = 1; i < pts.length; i++) m += dist(pts[i - 1], pts[i]);
  if (closed && pts.length > 2) m += dist(pts[pts.length - 1], pts[0]);
  return m;
}

/**
 * (a loop with a tail) The point of a line nearest p, leaving out the stretch within `min` m along the line from its
 * `end` ('start' | 'end'): where that end may meet its own line. { p, i (the stretch i..i+1), t, d, s (m from the start) }
 * or null (no stretch left). @param {Pts} pts
 */
export function meetOwn(pts, p, end = 'end', min = 10) {
  const n = pts.length, cum = [0];
  for (let i = 1; i < n; i++) cum.push(cum[i - 1] + dist(pts[i - 1], pts[i]));
  const tot = cum[n - 1];
  let best = null;
  for (let i = 0; i < n - 1; i++) {
    if (end === 'end' ? tot - cum[i + 1] < min : cum[i] < min) continue;
    const [ax, az] = pts[i], [bx, bz] = pts[i + 1], t = stretchT(p[0], p[1], ax, az, bx, bz), q = [ax + (bx - ax) * t, az + (bz - az) * t], d = dist(q, p);
    if (!best || d < best.d) best = { p: q, i, t, d, s: cum[i] + (cum[i + 1] - cum[i]) * t };
  }
  return best;
}
/**
 * (a loop with a tail) A line whose end comes back onto its own line, within `tol` m and more than `min` m along from
 * it (a trail out to a ring and round it back to where it left), or whose start does: walked out along the tail, round
 * the ring and back. Returns { route: [the free end ... the meeting point, round the ring to it, ... back to the free
 * end] (its last point = its first), ring: the ring alone (last = first), tail: m from the free end to the meeting point,
 * end: which end of the line meets it } or null (a plain loop, an open line, or a tail under 0.5 m: a loop).
 * @param {Pts} pts
 */
export function tailLoop(pts, tol = 1, min = 10) {
  if (!Array.isArray(pts) || pts.length < 4 || dist(pts[0], pts[pts.length - 1]) < 0.05) return null;
  for (const end of ['end', 'start']) {
    const L = end === 'end' ? pts : [...pts].reverse(), m = meetOwn(L, L[L.length - 1], 'end', min);
    if (!m || m.d > tol || m.s < 0.5) continue;
    const tail = [...L.slice(0, m.i + 1), m.p].filter((q, k, a) => !k || dist(q, a[k - 1]) > 0.01);
    const ring = [m.p, ...L.slice(m.i + 1, -1), m.p].filter((q, k, a) => !k || dist(q, a[k - 1]) > 0.01);
    if (ring.length < 4) continue;
    return { route: [...tail, ...ring.slice(1), ...tail.slice(0, -1).reverse()], ring, tail: m.s, end };
  }
  return null;
}

/** A polyline resampled every `step` m (each stretch cut into equal pieces no longer than `step`; every vertex kept). */
export function resample(pts, step = 1) {
  const out = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    const [ax, az] = pts[i - 1], [bx, bz] = pts[i], L = Math.hypot(bx - ax, bz - az), n = Math.max(1, Math.ceil(L / step));
    for (let k = 1; k <= n; k++) out.push([ax + ((bx - ax) * k) / n, az + ((bz - az) * k) / n]);
  }
  return out;
}

/**
 * VIS-228: a polyline resampled at equal steps along it, no longer than `step` m, its two ends kept (its corners are
 * not: give it rounded corners, bandLine). Rows of a band drawn along it are then evenly spaced (resample keeps every
 * vertex, so a vertex just past a cut made a sliver of a row).
 * @param {Pts} pts
 * @param {number} step
 * @returns {Pt[]}
 */
export function resampleEven(pts, step = 1) {
  if (!(pts?.length > 1)) return (pts || []).map((q) => [q[0], q[1]]);
  const L = lengthOf(pts), n = Math.max(1, Math.ceil(L / step)), ds = L / n;
  /** @type {Pt[]} */
  const out = [[pts[0][0], pts[0][1]]];
  let i = 1, s0 = 0, seg = dist(pts[0], pts[1]);
  for (let k = 1; k < n; k++) {
    const s = k * ds;
    while (i < pts.length - 1 && s0 + seg < s) { s0 += seg; i++; seg = dist(pts[i - 1], pts[i]); }
    const t = seg > 0 ? Math.min(1, (s - s0) / seg) : 0, [ax, az] = pts[i - 1], [bx, bz] = pts[i];
    out.push([ax + (bx - ax) * t, az + (bz - az) * t]);
  }
  out.push([pts[pts.length - 1][0], pts[pts.length - 1][1]]);
  return out;
}

/**
 * VIS-228: a line made fit to draw as a band that reaches `reach` m to each side of it (a road with its plough banks, a
 * path with its soft edge), so that the band never folds over itself:
 *   - points closer than 5 cm merged;
 *   - a corner where the line doubles back (turns more than 100 degrees) next to a stretch shorter than `reach` dropped,
 *     and a tail shorter than `reach` / 2 after a corner of more than 60 degrees cut: the hooks and zig-zags a hand
 *     leaves at the end of a drawn path or where it was dragged back (they made the crossed wedges of folded banks);
 *   - every corner rounded with an arc of radius `r` (default `reach`; less where its two stretches are too short), in
 *     pieces no longer than `step` m: the inside of a bend no sharper than the band is wide never folds.
 * A loop (first and last point within 5 cm) has no ends: its closing corner is rounded too, and it stays closed.
 * `soft` (m): a line drawn by hand is eased instead of its corners rounded (easeLine, sigma `soft`): its points sit a
 * metre or two apart, so a corner's arc could be no wider than half a stretch and its clicks showed as kinks.
 * Returns a new array (the input is not changed); fewer than 2 points come back as they are.
 * @param {Pts} pts
 * @param {number} reach
 * @param {number} [r]
 * @param {number} [step]
 * @param {number} [soft]
 * @returns {Pt[]}
 */
export function bandLine(pts, reach, r = reach, step = 1, soft = 0) {
  /** @type {Pt[]} */
  let p = [];
  for (const q of pts || []) if (!p.length || dist(p[p.length - 1], q) > 0.05) p.push([q[0], q[1]]);
  if (p.length < 2) return (pts || []).map((q) => [q[0], q[1]]);
  const loop = p.length > 3 && dist(p[0], p[p.length - 1]) < 0.05;
  if (loop) p[p.length - 1] = [p[0][0], p[0][1]];
  const cosAt = (a, b, c) => { const ux = b[0] - a[0], uz = b[1] - a[1], vx = c[0] - b[0], vz = c[1] - b[1]; return (ux * vx + uz * vz) / (Math.hypot(ux, uz) * Math.hypot(vx, vz) || 1); };
  // hooks and zig-zags: a corner that doubles back on a short stretch goes (again until none is left)
  for (let i = 1; i < p.length - 1 && p.length > 2;) {
    if (cosAt(p[i - 1], p[i], p[i + 1]) < -0.17 && Math.min(dist(p[i - 1], p[i]), dist(p[i], p[i + 1])) < reach) { p.splice(i, 1); i = Math.max(1, i - 1); } else i++;
  }
  if (!loop) for (const flip of [false, true]) { // a short tail after a sharp corner, at each end
    if (flip) p.reverse();
    for (let cut = true; cut && p.length > 2;) {
      cut = false;
      for (let i = p.length - 2, tail = 0; i >= 1; i--) {
        tail += dist(p[i], p[i + 1]);
        if (tail >= reach / 2) break;
        if (cosAt(p[i - 1], p[i], p[i + 1]) < 0.5) { p = p.slice(0, i + 1); cut = true; break; }
      }
    }
    if (flip) p.reverse();
  }
  if (soft > 0 && p.length > 2) return easeLine(p, soft);
  if (!(r > 0) || p.length < 3) return p;
  // round each corner: an arc tangent to both stretches, its tangent length no more than half of either stretch
  const n = p.length, m = loop ? n - 1 : n; // a loop's corners: 0 .. n-2 (point n-1 is point 0 again)
  /** @type {Pt[]} */
  const out = [];
  for (let i = 0; i < m; i++) {
    const a = loop ? p[(i - 1 + m) % m] : p[i - 1], b = p[i], c = loop ? p[(i + 1) % m] : p[i + 1];
    if (!a || !c) { out.push(b); continue; } // an end of an open line
    const la = dist(a, b), lc = dist(b, c), ux = (b[0] - a[0]) / la, uz = (b[1] - a[1]) / la, vx = (c[0] - b[0]) / lc, vz = (c[1] - b[1]) / lc;
    const th = Math.acos(Math.max(-1, Math.min(1, ux * vx + uz * vz))), side = Math.sign(ux * vz - uz * vx);
    if (th < 1e-3 || !side) { out.push(b); continue; }
    // (a slight turn spreads over `step` x 3 at least: a 5 degree kink of a drawn path turned within 0.3 m, a notch)
    const tan = Math.tan(th / 2), t = Math.min(Math.max(r * tan, step * 1.5), la / 2, lc / 2), re = t / tan;
    const sx = b[0] - ux * t, sz = b[1] - uz * t, ox = sx - uz * side * re, oz = sz + ux * side * re; // arc start, centre
    const k = Math.max(1, Math.ceil((re * th) / step));
    for (let j = 0; j <= k; j++) {
      const f = (side * th * j) / k, cs = Math.cos(f), sn = Math.sin(f), dx = sx - ox, dz = sz - oz;
      out.push([ox + dx * cs - dz * sn, oz + dx * sn + dz * cs]);
    }
  }
  if (loop) out.push([out[0][0], out[0][1]]);
  // two arcs that meet half way along a stretch leave a point twice (or 1 cm apart, a kink of any direction): one goes
  const fin = [out[0]];
  for (let i = 1; i < out.length; i++) if (dist(fin[fin.length - 1], out[i]) > 0.05) fin.push(out[i]); else if (i === out.length - 1) fin[fin.length - 1] = out[i];
  return fin;
}

/**
 * A line eased into a smooth curve: points every 0.5 m along it, each moved to the Gaussian-weighted mean of its
 * neighbours along the line (sigma `sigma` m, out to 2.5 sigma), so that every corner becomes a bend some `sigma` m
 * round however close together its points are. An open line keeps its two ends and its direction there (the line is
 * mirrored through each end); a loop (first and last point within 5 cm) goes round through the join and stays closed.
 * @param {Pts} pts
 * @param {number} sigma
 * @returns {Pt[]}
 */
export function easeLine(pts, sigma) {
  if (!(pts?.length > 2) || !(sigma > 0)) return (pts || []).map((q) => [q[0], q[1]]);
  const loop = pts.length > 3 && dist(pts[0], pts[pts.length - 1]) < 0.05;
  const q = resampleEven(loop ? [...pts.slice(0, -1), pts[0]] : pts, 0.5), ds = lengthOf(q) / (q.length - 1 || 1);
  const n = loop ? q.length - 1 : q.length, K = Math.max(1, Math.ceil((2.5 * sigma) / ds)), w = [];
  for (let k = 0; k <= K; k++) w.push(Math.exp(-((k * ds) ** 2) / (2 * sigma * sigma)));
  const at = (i, c) => { // point i of the line, its loop or its ends' mirrors
    if (loop) return q[((i % n) + n) % n][c];
    if (i < 0) return 2 * q[0][c] - q[Math.min(n - 1, -i)][c];
    if (i >= n) return 2 * q[n - 1][c] - q[Math.max(0, 2 * (n - 1) - i)][c];
    return q[i][c];
  };
  /** @type {Pt[]} */
  const out = [];
  for (let i = 0; i < n; i++) {
    if (!loop && (i === 0 || i === n - 1)) { out.push([q[i][0], q[i][1]]); continue; }
    let x = 0, z = 0, s = 0;
    for (let k = -K; k <= K; k++) { const f = w[Math.abs(k)]; x += f * at(i + k, 0); z += f * at(i + k, 1); s += f; }
    out.push([x / s, z / s]);
  }
  if (loop) out.push([out[0][0], out[0][1]]);
  return out;
}

/**
 * WS-81: how many bulbs a fence of `length` m (its lines' length on the ground, all of them) gets with bulbs `every` m
 * when they go evenly along the whole fence. No spacing (0, unset) = the default 1.1 m. The map (rides.js) and the
 * portal's Fences panel both count with this, from the length rounded to the metre.
 */
export const fenceBulbCount = (length, every) => Math.max(1, Math.round(Math.round(length) / (every || 1.1)));
